import type {
  FlightPlayerScoresFragment,
  HoleScoreFragment,
} from '../../__generated__/hooks'
import { holeStrokesFor } from '../../util/strokes'

export const firstName = (full: string): string => full.split(/\s+/)[0] ?? full

export const sumForHoles = (
  entry: FlightPlayerScoresFragment,
  holeNumbers: Set<number>
): number =>
  (entry.scores ?? []).reduce(
    (acc, s) =>
      holeNumbers.has(s.hole.number) && s.score != null ? acc + s.score : acc,
    0
  )

export const parForHoles = (holes: HoleScoreFragment[]): number =>
  holes.reduce((s, h) => s + h.hole.par, 0)

/** Sum of stableford points across the given holes (scored holes only). */
export const sumStablefordForHoles = (
  entry: FlightPlayerScoresFragment,
  holeNumbers: Set<number>
): number =>
  (entry.scores ?? []).reduce(
    (acc, s) =>
      holeNumbers.has(s.hole.number) && s.score != null
        ? acc + s.stablefordPoints
        : acc,
    0
  )

/**
 * Strokes this player receives on the scored holes within `holeNumbers`.
 *
 * Counted only on SCORED holes so a part-played segment nets against what was
 * actually played — otherwise an unfinished front nine subtracts strokes for
 * holes nobody has stood on yet.
 *
 * Always the player's OWN course-handicap allocation. In a match event the
 * score card shows the match differential instead, but this is the individual
 * competition, which is scored off each player's own allowance.
 */
export const sumStrokesForHoles = (
  entry: FlightPlayerScoresFragment,
  holeNumbers: Set<number>
): number => {
  return (entry.scores ?? []).reduce(
    (acc, s) =>
      holeNumbers.has(s.hole.number) && s.score != null
        ? acc + holeStrokesFor(null, s, s.hole.index)
        : acc,
    0
  )
}

/**
 * What a block asks of one side, and what the side has returned.
 *
 * A split round's blocks disagree about which rows count, so a side's figure
 * cannot be read off a column: a one-ball block counts the team's six, best
 * ball keeps the better of each pair's two, and a singles block counts both
 * partners. The rows to sum are therefore chosen per block, not per entry.
 *
 * Net comes from `netScore` rather than gross-minus-allowance, because that is
 * the number the leaderboard sums. The two agree, and taking the same road
 * there keeps them from drifting apart.
 */
export const blockSideTotals = (
  entries: FlightPlayerScoresFragment[],
  block: { fromHole: number; toHole: number; format: string }
): { gross: number; net: number; points: number; counted: number } => {
  const inBlock = (n: number) => n >= block.fromHole && n <= block.toHole
  // Best ball's counting rows are the side's picks, which the API scopes to
  // the block; every other block counts every row it owns.
  const rows =
    block.format === 'BESTBALL'
      ? (entries[0]?.teamScores ?? []).filter((s) => inBlock(s.hole.number))
      : entries.flatMap((e) =>
          (e.scores ?? []).filter((s) => inBlock(s.hole.number))
        )
  const scored = rows.filter((s) => s.score != null)
  return {
    gross: scored.reduce((sum, s) => sum + (s.score ?? 0), 0),
    net: scored.reduce((sum, s) => sum + (s.netScore ?? s.score ?? 0), 0),
    points: scored.reduce((sum, s) => sum + s.stablefordPoints, 0),
    counted: scored.length,
  }
}
